import React from 'react'
import { FormattedMessage } from 'react-intl'
import { DownshiftProps, PropGetters } from 'downshift'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, MenuItem } from '@material-ui/core'
import {
  structureSearchDrawRoute,
  searchResultsRoute,
  useRouter,
} from '@src/routes'
import { Link } from '@src/components/Link'
import {
  StartingIndexes,
  GetIndex,
  SearchOptions,
} from './useHeaderAutoSuggest'
import { sendSearchInteractionEvent } from '@src/utils/analytics'
import getConfig from 'next/config'

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

const useStyles = makeStyles((theme: Theme) => ({
  searchLinksWrapper: {
    padding: theme.spacing(2, 0, 2),
  },
  searchLink: {
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(0.75, 4),
  },
}))

interface HeaderSearchLinksProps {
  getItemProps: PropGetters<SearchOptions>['getItemProps']
  highlightedIndex: DownshiftProps<SearchOptions>['highlightedIndex']
  getItemIndex: GetIndex
  showSuggestions: boolean
}

const HeaderSearchLinks: React.FC<HeaderSearchLinksProps> = ({
  getItemProps,
  highlightedIndex,
  getItemIndex,
  showSuggestions,
}) => {
  const classes = useStyles()
  const router = useRouter()

  // DownshiftJS requires passing an index to all dropdown items
  // Since we are including the static links as suggestions we must derive
  // starting index based on if suggestions are being show
  // and/or the number of suggestions and then increment index for every static link
  const startingIndex = getItemIndex(StartingIndexes.LINKS, 0, showSuggestions)

  return (
    <div className={classes.searchLinksWrapper}>
      <MenuItem
        {...getItemProps({
          item: { item: '', href: searchResultsRoute.index() },
          index: startingIndex,
          selected: startingIndex === highlightedIndex,
          className: classes.searchLink,
        })}
        onClick={() => {
          sendSearchInteractionEvent({
            action: 'visit advanced search page',
            component: 'autosuggest',
            elementType: 'link',
            elementText: 'advanced search',
            linkUrl: searchResultsRoute.index(),
          })
          router.push(searchResultsRoute.index())
        }}
      >
        <Link href={searchResultsRoute.index()}>
          <a>
            <FormattedMessage
              id="ADVANCED_SEARCH"
              defaultMessage="Advanced Search"
            />
          </a>
        </Link>
      </MenuItem>

      {featureFlags.structureSearch && (
        <MenuItem
          {...getItemProps({
            item: { item: '', href: structureSearchDrawRoute.index() },
            index: startingIndex + 1,
            selected: startingIndex + 1 === highlightedIndex,
            className: classes.searchLink,
          })}
          onClick={() => {
            sendSearchInteractionEvent({
              action: 'visit chemical structure search',
              component: 'autosuggest',
              elementType: 'link',
              elementText: 'chemical structure search',
              linkUrl: structureSearchDrawRoute.index(),
            })
            router.push(structureSearchDrawRoute.index())
          }}
        >
          <Link href={structureSearchDrawRoute.index()}>
            <a>
              <FormattedMessage
                id="CHEMICAL_STRUCTURE_SEARCH"
                defaultMessage="Chemical Structure Search"
              />
            </a>
          </Link>
        </MenuItem>
      )}
    </div>
  )
}

export default HeaderSearchLinks
